/**
 * #1696: "erro" e "lista vazia" viravam a mesma tela.
 *
 * Quando o backend não responde, os hooks trocam o erro por `[]` (o clássico
 * `data?.itens ?? []`), a tabela recebe lista vazia e escreve "nenhum registro".
 * Quem opera lê isso como "não há trabalho" e vai embora com a fila cheia.
 *
 * A correção é na origem: toda chamada do sistema sai de uma única instância de
 * axios, então o interceptor dela é o lugar onde a falha ainda existe antes de
 * alguém apagá-la. Aqui ela fica guardada até a próxima carga dar certo.
 */
type Falha = { mensagem: string };

let falha: Falha | null = null;
const ouvintes = new Set<() => void>();

const avisar = () => ouvintes.forEach((o) => o());

export function registrarFalhaDeCarga(mensagem: string) {
	falha = { mensagem };
	avisar();
}

/** Carga bem-sucedida apaga o aviso — não faz sentido avisar do que já voltou. */
export function limparFalhaDeCarga() {
	if (!falha) return;
	falha = null;
	avisar();
}

export function obterFalhaDeCarga(): Falha | null {
	return falha;
}

export function assinarFalhaDeCarga(ouvinte: () => void): () => void {
	ouvintes.add(ouvinte);
	return () => {
		ouvintes.delete(ouvinte);
	};
}

type ErroDeRequest = {
	code?: string;
	config?: { method?: string };
	response?: { status?: number };
};

export function registrarSeFalhaDeCarga(erro: ErroDeRequest): boolean {
	if (erro?.code === "ERR_CANCELED") return false;

	const ehLeitura = (erro?.config?.method ?? "get").toLowerCase() === "get";
	if (!ehLeitura) return false;

	const status = erro?.response?.status;
	if (erro?.response && !(status !== undefined && status >= 500)) return false;

	registrarFalhaDeCarga(
		erro?.response
			? "O servidor não conseguiu responder. Os dados abaixo podem estar incompletos."
			: "Sem conexão com o servidor. Os dados abaixo podem estar desatualizados.",
	);
	return true;
}
